import React, { useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Badge,
  Divider,
  Card,
  Button,
  ActionIcon,
  Title,
  Alert,
} from '@mantine/core';
import { IconPlus, IconTrash, IconLayersLinked } from '@tabler/icons-react';

import { RecordWithMetadata, TemplateDoc, FieldWithOverride, ArrayFieldGroup, DefaultArrayItem } from '../../../types/geneva-schema';
import { GenevaApiItem } from '../../../hooks/useGenevaLookup';
import { GenevaFieldRenderer } from '../../../components/GenevaFieldRenderer';
import { PortfolioPositionTable } from '../../../components/PortfolioPositionTable';
import { separateArrayFields, getArrayFieldInfo, createDefaultArrayItem, resolveFieldOverridesRecursively, safeEvaluateExpression } from '../../../utils/geneva-schema-helpers';

type DefaultItemFieldSetting = NonNullable<DefaultArrayItem['fieldSettings']>[string];

interface GenevaRecordFormProps {
  record: RecordWithMetadata;
  template: TemplateDoc;
  formData: Record<string, any>;
  onFieldChange: (fieldName: string, value: any) => void;
  readonly?: boolean;
  onSupportingDataChange?: (fieldName: string, item: GenevaApiItem | null) => void;
}

const getArrayFieldColumnStyle = (field: FieldWithOverride): React.CSSProperties => {
  const fieldName = field.xmlTag.toLowerCase();
  const fieldType = field.type.toLowerCase();

  if (fieldName === 'linenumber' || fieldName === 'line' || fieldName === 'index' || fieldName === 'rownumber') {
    return { flex: '0 0 96px', maxWidth: 96 };
  }

  if (fieldType === 'integer' || fieldType === 'number' || fieldType === 'real') {
    return { flex: '0 0 160px', maxWidth: 180 };
  }

  if ((field.links && field.links.length > 0) || field.references) {
    return { flex: '1 1 280px', minWidth: 240 };
  }

  return { flex: '1 1 180px', minWidth: 140 };
};

export const GenevaRecordForm: React.FC<GenevaRecordFormProps> = ({
  record,
  template,
  formData,
  onFieldChange,
  readonly = false,
  onSupportingDataChange,
}) => {
  // Resolve overrides once for this record (used across the component)
  const recordOverrides = useMemo(() => {
    return resolveFieldOverridesRecursively(record.record, template);
  }, [record.record, template]);



  // Separate regular fields from array fields
  const { regularFields, arrayFieldGroups } = useMemo(() => {
    return separateArrayFields(record);
  }, [record]);

  const fieldsWithOverrides = useMemo((): FieldWithOverride[] => {
    // Only process regular fields here, array fields are handled separately
    return regularFields.map(field => {
      const override = recordOverrides[field.xmlTag];
      return {
        ...field,
        override,
        isOverridden: !!override && Object.keys(override).length > 0,
      };
    }).filter(field => {
      // Hide fields marked as hidden
      return !field.override?.hidden;
    });
  }, [regularFields, recordOverrides]);

  useEffect(() => {
    fieldsWithOverrides.forEach(field => {
      if (field.override?.computed) {
        return;
      }

      if (field.override?.default !== undefined) {
        const currentValue = formData[field.xmlTag];
        if (currentValue === undefined) {
          onFieldChange(field.xmlTag, field.override.default);
        }
      }
    });
  }, [fieldsWithOverrides, formData, onFieldChange]);

  const getFieldValue = (field: FieldWithOverride) => {
    const currentValue = formData[field.xmlTag];

    // If no current value, check for computed expression
    if (currentValue === undefined && field.override?.computed) {
      return evaluateComputedExpression(field.override.computed.expr, formData);
    }

    // If still no value, use default
    if (currentValue === undefined && field.override?.default !== undefined) {
      return field.override.default;
    }

    return currentValue || '';
  };

  const evaluateComputedExpression = (expr: string, data: Record<string, any>): any => {
    try {
      // Simple expression evaluation - replace field names with values
      let evaluatedExpr = expr;

      // Replace field references with actual values
      Object.keys(data).forEach(fieldName => {
        const value = data[fieldName];
        if (value !== undefined) {
          // Replace field name with quoted value if it's a string
          const quotedValue = typeof value === 'string' ? `"${value}"` : String(value);
          evaluatedExpr = evaluatedExpr.replace(new RegExp(`\\b${fieldName}\\b`, 'g'), quotedValue);
        }
      });

      // For safety, only allow simple expressions (no eval)
      // This is a simplified version - in production you'd want a proper expression parser
      if (evaluatedExpr.includes('Date.now()')) {
        return Date.now();
      }

      // Simple string concatenation
      if (evaluatedExpr.includes('+')) {
        return evaluatedExpr; // Return as-is for now, would need proper parser
      }

      return evaluatedExpr;
    } catch (error) {
      console.warn('Failed to evaluate computed expression:', expr, error);
      return expr; // Return original expression if evaluation fails
    }
  };

  const isFieldRequired = (field: FieldWithOverride): boolean => {
    return field.override?.required ?? field.required;
  };

  const isFieldReadOnly = (field: FieldWithOverride): boolean => {
    return readonly || (field.override?.readOnly ?? false);
  };

  const renderField = (field: FieldWithOverride) => {
    const value = getFieldValue(field);
    const required = isFieldRequired(field);
    const readOnly = isFieldReadOnly(field);

    return (
      <GenevaFieldRenderer
        key={field.xmlTag}
        field={field}
        value={value}
        onChange={onFieldChange}
        onLookupValueChange={onSupportingDataChange}
        readOnly={readOnly}
        required={required}
        showLabel={true}
        showDescription={true}
        showOverrideBadges={true}
      />
    );
  };

  // Helper function to identify index fields
  const isIndexField = (field: any): boolean => {
    const fieldName = field.xmlTag || field.name || '';
    return fieldName === 'Index' || fieldName.endsWith('Number') || fieldName.toLowerCase().includes('index');
  };

  // Helper function to auto-set index values for an array item
  const setIndexValues = (item: any, arrayGroup: ArrayFieldGroup, itemIndex: number): any => {
    const updatedItem = { ...item };

    arrayGroup.fields.forEach(field => {
      if (isIndexField(field)) {
        const arrayInfo = getArrayFieldInfo(field);
        if (arrayInfo) {
          updatedItem[arrayInfo.fieldName] = itemIndex + 1; // 1-based index
        }
      }
    });

    return updatedItem;
  };

  const evaluateDefaultArrayItemValues = useCallback(async (
    defaultItem: DefaultArrayItem,
    baseValues: Record<string, any>,
    globalContext: Record<string, any>
  ): Promise<{ values: Record<string, any>; changed: boolean }> => {
    if (!defaultItem?.computedExpressions || Object.keys(defaultItem.computedExpressions).length === 0) {
      return { values: baseValues, changed: false };
    }

    let changed = false;
    let updatedValues = { ...baseValues };

    for (const [fieldName, expr] of Object.entries(defaultItem.computedExpressions)) {
      const context = {
        ...globalContext,
        ...updatedValues,
      };

      const evaluation = await safeEvaluateExpression(expr, context);
      if (evaluation.success) {
        const nextValue = evaluation.result ?? '';
        if (updatedValues[fieldName] !== nextValue) {
          updatedValues[fieldName] = nextValue;
          changed = true;
        }
      } else {
        console.warn(`Failed to evaluate default array expression for ${fieldName}:`, evaluation.error);
      }
    }

    return {
      values: updatedValues,
      changed,
    };
  }, []);

  const getDefaultItemIdentifier = (arrayName: string, defaultItem: DefaultArrayItem, index: number) =>
    defaultItem.id || `${arrayName}:${index}`;

  // Array field management
  const handleArrayItemAdd = (arrayName: string) => {
    const arrayGroup = arrayFieldGroups.find(g => g.arrayName === arrayName);
    if (!arrayGroup) return;

    const newItem = createDefaultArrayItem(arrayGroup);
    const currentArray = formData[arrayName] || [];
    const itemIndex = currentArray.length;

    // Auto-set index values
    const itemWithIndex = setIndexValues(newItem, arrayGroup, itemIndex);

    onFieldChange(arrayName, [...currentArray, itemWithIndex]);
  };

  const handleArrayItemRemove = (arrayName: string, index: number) => {
    const arrayGroup = arrayFieldGroups.find(g => g.arrayName === arrayName);
    const currentArray = formData[arrayName] || [];
    const filteredArray = currentArray.filter((_: any, i: number) => i !== index);

    // Re-index remaining items if we have an array group
    if (arrayGroup) {
      const reIndexedArray = filteredArray.map((item: any, newIndex: number) =>
        setIndexValues(item, arrayGroup, newIndex)
      );
      onFieldChange(arrayName, reIndexedArray);
    } else {
      onFieldChange(arrayName, filteredArray);
    }
  };

  const handleArrayItemChange = (arrayName: string, index: number, fieldName: string, value: any) => {
    const currentArray = formData[arrayName] || [];
    const newArray = [...currentArray];
    if (!newArray[index]) newArray[index] = {};
    newArray[index][fieldName] = value;
    onFieldChange(arrayName, newArray);
  };

  // Initialize array fields with default items - moved to top level to avoid conditional hook calls
  useEffect(() => {
    let cancelled = false;

    const initializeDefaultArrayItems = async () => {
      for (const arrayGroup of arrayFieldGroups) {
        const arrayData = formData[arrayGroup.arrayName] || [];
        const firstField = arrayGroup.fields[0];
        if (!firstField) continue;

        // Use composite key for array field - no fallback to avoid conflicts with regular fields
        const compositeKey = `${arrayGroup.arrayName}.${firstField.xmlTag}`;
        const firstFieldOverride = recordOverrides[compositeKey];
        const defaultArrayItems = firstFieldOverride?.defaultArrayItems;

        if (arrayData.length === 0 && defaultArrayItems && defaultArrayItems.length > 0) {
          const processedItems: any[] = [];

          for (let index = 0; index < defaultArrayItems.length; index++) {
            const defaultItem = defaultArrayItems[index];
            const baseValues = { ...(defaultItem.values || {}) };
            const evaluation = await evaluateDefaultArrayItemValues(defaultItem, baseValues, formData);
            const defaultItemId = getDefaultItemIdentifier(arrayGroup.arrayName, defaultItem, index);
            const evaluatedValues = {
              ...evaluation.values,
              _defaultItemId: defaultItemId,
            };
            processedItems.push(setIndexValues(evaluatedValues, arrayGroup, index));
          }

          if (!cancelled) {
            onFieldChange(arrayGroup.arrayName, processedItems);
          }
        }
      }
    };

    initializeDefaultArrayItems();

    return () => {
      cancelled = true;
    };
  }, [arrayFieldGroups, formData, recordOverrides, onFieldChange, evaluateDefaultArrayItemValues]);

  useEffect(() => {
    let cancelled = false;

    const recomputeArrayExpressions = async () => {
      for (const arrayGroup of arrayFieldGroups) {
        const arrayData = formData[arrayGroup.arrayName];
        if (!Array.isArray(arrayData) || arrayData.length === 0) {
          continue;
        }

        const firstField = arrayGroup.fields[0];
        if (!firstField) continue;

        const arrayInfo = getArrayFieldInfo(firstField);
        const overrideKey = arrayInfo ? `${arrayGroup.arrayName}.${firstField.xmlTag}` : firstField.xmlTag;
        const firstFieldOverride = recordOverrides[overrideKey] || recordOverrides[firstField.xmlTag];
        const defaultArrayItems = firstFieldOverride?.defaultArrayItems;

        if (!defaultArrayItems || defaultArrayItems.length === 0) {
          continue;
        }

        let updatedArray = arrayData;
        let hasChanges = false;

        for (let index = 0; index < defaultArrayItems.length; index++) {
          const defaultItem = defaultArrayItems[index];
          if (!defaultItem || !defaultItem.computedExpressions || Object.keys(defaultItem.computedExpressions).length === 0) {
            continue;
          }

          const currentItem = updatedArray[index];
          if (!currentItem) {
            continue;
          }

          const evaluation = await evaluateDefaultArrayItemValues(defaultItem, currentItem, formData);
          if (evaluation.changed) {
            if (!hasChanges) {
              updatedArray = [...updatedArray];
              hasChanges = true;
            }
            const mergedValues = {
              ...currentItem,
              ...evaluation.values,
            };
            if (currentItem._defaultItemId && !mergedValues._defaultItemId) {
              mergedValues._defaultItemId = currentItem._defaultItemId;
            }
            updatedArray[index] = mergedValues;
          }
        }

        if (hasChanges && !cancelled) {
          onFieldChange(arrayGroup.arrayName, updatedArray);
        }
      }
    };

    recomputeArrayExpressions();

    return () => {
      cancelled = true;
    };
  }, [arrayFieldGroups, formData, recordOverrides, onFieldChange, evaluateDefaultArrayItemValues]);

  // Check if we have a Portfolio field and get its value
  const portfolioField = fieldsWithOverrides.find(field =>
    field.xmlTag.toLowerCase() === 'portfolio' ||
    field.links?.some(link => link.toLowerCase().includes('portfolio'))
  );
  const portfolioValue = portfolioField ? formData[portfolioField.xmlTag] : null;

  // Check if we have an EventDate field and get its value
  const eventDateField = fieldsWithOverrides.find(field =>
    field.xmlTag.toLowerCase() === 'eventdate' ||
    field.xmlTag.toLowerCase() === 'date' ||
    field.type.toLowerCase().includes('date')
  );
  const eventDateValue = eventDateField ? formData[eventDateField.xmlTag] : null;

  // Get current Investment and LocationAccount values for highlighting
  const investmentField = fieldsWithOverrides.find(field =>
    field.xmlTag.toLowerCase() === 'investment' ||
    field.links?.some(link => link && link.toLowerCase().includes('investment'))
  );
  const locationAccountField = fieldsWithOverrides.find(field =>
    field.xmlTag.toLowerCase() === 'locationaccount' ||
    field.xmlTag.toLowerCase() === 'custodianaccount' ||
    field.links?.some(link => link && (link.toLowerCase().includes('account') || link.toLowerCase().includes('custodian')))
  );

  const currentInvestment = investmentField ? formData[investmentField.xmlTag] : null;
  const currentLocationAccount = locationAccountField ? formData[locationAccountField.xmlTag] : null;

  // Get current Quantity value for validation
  const quantityField = fieldsWithOverrides.find(field =>
    field.xmlTag.toLowerCase() === 'quantity' ||
    field.xmlTag.toLowerCase() === 'qty' ||
    field.xmlTag.toLowerCase() === 'netcounteramount' ||
    field.xmlTag.toLowerCase().includes('quantity')
  );
  const currentQuantity = quantityField ? formData[quantityField.xmlTag] : null;

  const enableQuantityWarning = Boolean(template.recordQuantityChecks?.[record.record]);

  // Debug logging for date changes
  React.useEffect(() => {
    if (eventDateField) {
      console.log(`[GenevaRecordForm] Event date field: ${eventDateField.xmlTag}, value: ${eventDateValue}`);
    }
  }, [eventDateValue, eventDateField]);

  // Handle portfolio position row click
  const handlePositionRowClick = (investment: string, custodianAccount: string) => {
    // Find Investment field and populate it
    const investmentField = fieldsWithOverrides.find(field =>
      field.xmlTag.toLowerCase() === 'investment' ||
      field.links?.some(link => link && link.toLowerCase().includes('investment'))
    );

    if (investmentField && investment) {
      onFieldChange(investmentField.xmlTag, investment);
    }

    // Find LocationAccount field and populate it
    const locationAccountField = fieldsWithOverrides.find(field =>
      field.xmlTag.toLowerCase() === 'locationaccount' ||
      field.xmlTag.toLowerCase() === 'custodianaccount' ||
      field.links?.some(link => link && (link.toLowerCase().includes('account') || link.toLowerCase().includes('custodian')))
    );

    if (locationAccountField && custodianAccount) {
      onFieldChange(locationAccountField.xmlTag, custodianAccount);
    }
  };

  const renderArrayField = (arrayGroup: ArrayFieldGroup) => {
    // Check if any field in the array group is not hidden (show array if at least one field is visible)
    const visibleFields = arrayGroup.fields.filter(field => {
      const arrayInfo = getArrayFieldInfo(field);
      const overrideKey = arrayInfo ? `${arrayGroup.arrayName}.${field.xmlTag}` : field.xmlTag;
      const fieldOverride = recordOverrides[overrideKey] || recordOverrides[field.xmlTag];
      return !fieldOverride?.hidden;
    });

    // Hide entire array if no fields are visible
    if (visibleFields.length === 0) {
      return null;
    }

    // Get array data
    const arrayData = formData[arrayGroup.arrayName] || [];
    const firstField = arrayGroup.fields[0];
    const firstFieldOverride = (() => {
      if (!firstField) return undefined;
      const arrayInfo = getArrayFieldInfo(firstField);
      const overrideKey = arrayInfo ? `${arrayGroup.arrayName}.${firstField.xmlTag}` : firstField.xmlTag;
      return recordOverrides[overrideKey] || recordOverrides[firstField.xmlTag];
    })();

    const defaultArrayItems = firstFieldOverride?.defaultArrayItems || [];
    const defaultFieldSettingsMap = new Map<string, Record<string, DefaultItemFieldSetting>>();
    defaultArrayItems.forEach((defaultItem, idx) => {
      if (!defaultItem.fieldSettings) return;
      const itemId = getDefaultItemIdentifier(arrayGroup.arrayName, defaultItem, idx);
      defaultFieldSettingsMap.set(itemId, defaultItem.fieldSettings);
    });

    return (
      <Card key={arrayGroup.arrayName} shadow="sm" p="md" withBorder>
        <Stack gap="sm">
          <Group justify="space-between" mb="xs">
            <Group gap="xs">
              <IconLayersLinked size={20} />
              <Title order={5}>{arrayGroup.arrayName}</Title>
              <Badge size="sm" variant="light">
                {arrayData.length} items
              </Badge>
              {visibleFields.length < arrayGroup.fields.length && (
                <Badge size="xs" variant="outline" color="orange">
                  {arrayGroup.fields.length - visibleFields.length} hidden
                </Badge>
              )}
            </Group>
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => handleArrayItemAdd(arrayGroup.arrayName)}
              disabled={readonly}
            >
              Add Item
            </Button>
          </Group>

          {arrayData.length === 0 ? (
            <Alert color="gray" variant="light">
              No items added yet. Click "Add Item" to create one.
            </Alert>
          ) : (
            <Stack gap="xs">
              {/* Header row with field labels */}
              <Group gap="xs" style={{ paddingLeft: '30px', paddingRight: '40px' }}>
                <Badge size="xs" variant="outline" style={{ width: '60px', visibility: 'hidden' }}>
                  Item
                </Badge>
                <Box style={{ display: 'flex', flex: 1, gap: '1rem' }}>
                  {visibleFields.map(field => {
                    const arrayInfo = getArrayFieldInfo(field);
                    const overrideKey = arrayInfo ? `${arrayGroup.arrayName}.${field.xmlTag}` : field.xmlTag;
                    const fieldOverride = recordOverrides[overrideKey] || recordOverrides[field.xmlTag];
                    const required = fieldOverride?.required ?? field.required;
                    const formatFieldName = (name: string): string => {
                      return name.replace(/([A-Z])/g, ' $1').trim();
                    };

                    return (
                      <Text
                        key={field.xmlTag}
                        size="sm"
                        fw={600}
                        style={{ ...getArrayFieldColumnStyle(field), overflowWrap: 'break-word' }}
                      >
                        {formatFieldName(field.xmlTag)}{required && ' *'}
                      </Text>
                    );
                  })}
                </Box>
              </Group>

              {/* Data rows */}
              {arrayData.map((item: any, index: number) => {
                const defaultId = item?._defaultItemId;
                const rowFieldSettings = defaultId ? defaultFieldSettingsMap.get(defaultId) : undefined;
                const rowVisibleFields = visibleFields.filter(field => {
                  const arrayInfo = getArrayFieldInfo(field);
                  if (!arrayInfo) return false;
                  const fieldSetting = rowFieldSettings?.[arrayInfo.fieldName];
                  return !fieldSetting?.hidden;
                });
                //  if all fields are hidden, hide the entire row 
                if (rowVisibleFields.length === 0) {
                  return null;
                }

                return (
                  <Group key={index} gap="xs" align="flex-start">
                    <Badge size="xs" variant="dot" style={{ width: '60px', marginTop: '8px' }}>
                      Item {index + 1}
                    </Badge>

                    <Box style={{ display: 'flex', flex: 1, gap: '1rem' }}>
                      {rowVisibleFields.map(field => {
                        const arrayInfo = getArrayFieldInfo(field);
                        if (!arrayInfo) return null;

                        const overrideKey = `${arrayGroup.arrayName}.${field.xmlTag}`;
                        const fieldOverride = recordOverrides[overrideKey] || recordOverrides[field.xmlTag];

                        const fieldSetting = rowFieldSettings?.[arrayInfo.fieldName];


                        // Get current value from the form data
                        let value = item[arrayInfo.fieldName];

                        // Apply defaults if no value exists
                        if (value === undefined && fieldOverride?.default !== undefined) {
                          value = fieldOverride.default;
                        }

                        // Ensure proper type for form display
                        if (field.type === 'integer' || field.type === 'real') {
                          if (value === undefined || value === null || value === '') {
                            value = 0;
                          } else if (typeof value === 'object' || Array.isArray(value)) {
                            value = 0;
                          } else if (typeof value !== 'number') {
                            const valueStr = String(value);
                            const parsed = parseFloat(valueStr);
                            value = isNaN(parsed) ? 0 : parsed;
                          }
                        } else {
                          if (value === undefined || value === null) {
                            value = '';
                          } else if (typeof value === 'object' || Array.isArray(value)) {
                            value = JSON.stringify(value);
                          } else {
                            value = String(value);
                          }
                        }

                        const required = (() => {
                          if (fieldSetting?.required === true) return true;
                          if (fieldSetting?.required === false) return false;
                          return fieldOverride?.required ?? field.required;
                        })();

                        const readOnly = (() => {
                          if (readonly) return true;
                          if (fieldSetting?.readOnly === true) return true;
                          if (fieldSetting?.readOnly === false) return false;
                          if (!defaultId) return false;
                          return !!fieldOverride?.readOnly;
                        })();

                        // Create a field with override for consistent rendering
                        const fieldWithOverride: FieldWithOverride = {
                          ...field,
                          override: fieldOverride,
                          isOverridden: !!fieldOverride && Object.keys(fieldOverride).length > 0,
                        };

                        // Use GenevaFieldRenderer for array fields
                        return (
                          <Box key={field.xmlTag} style={getArrayFieldColumnStyle(fieldWithOverride)}>
                            <GenevaFieldRenderer
                              field={fieldWithOverride}
                              value={value}
                              onChange={(_fieldName, newValue) =>
                                handleArrayItemChange(arrayGroup.arrayName, index, arrayInfo.fieldName, newValue)
                              }
                              onLookupValueChange={undefined}
                              readOnly={readOnly}
                              required={required}
                              showLabel={false}
                              showDescription={false}
                              showOverrideBadges={false}
                            />
                          </Box>
                        );
                      })}
                    </Box>

                    <ActionIcon
                      color="red"
                      size="sm"
                      onClick={() => handleArrayItemRemove(arrayGroup.arrayName, index)}
                      disabled={readonly}
                      style={{ marginTop: '5px' }}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Card>
    );
  };

  return (
    <Stack gap="md">
      {/* Record Header */}
      <Box>
        <Group gap="xs" mb="xs">
          <Text size="lg" fw={600}>
            {record.isVirtual ? record.label : record.record}
          </Text>
          {record.isVirtual && (
            <Badge variant="outline" color="blue" size="sm">
              extends {record.baseRecord}
            </Badge>
          )}
        </Group>
        {/* <Text size="sm" c="dimmed">
          {fieldsWithOverrides.length} fields • {fieldsWithOverrides.filter(f => isFieldRequired(f)).length} required
        </Text> */}
      </Box>

      <Divider />

      {/* Regular Form Fields */}
      {fieldsWithOverrides.length > 0 && (
        <Stack gap="md">
          <Title order={5}>Fields</Title>
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '1rem'
            }}
          >
            {fieldsWithOverrides.map(renderField)}
          </Box>
        </Stack>
      )}

      {/* Portfolio Position Table */}
      {portfolioValue && (
        <Stack gap="md">
          <Group justify="space-between" align="baseline">
            <Title order={5}>Portfolio Positions</Title>
            {eventDateValue ? (
              <Badge size="lg" variant="filled" color="yellow">
                AS OF {new Date(eventDateValue).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Badge>
            ) : (
              <Badge size="lg" variant="filled" color="gray">
                AS OF TODAY
              </Badge>
            )}
          </Group>
          <PortfolioPositionTable
            key={`${portfolioValue}-${eventDateValue || 'today'}`}
            portfolioCode={portfolioValue}
            date={eventDateValue}
            showTitle={false}
            onRowClick={handlePositionRowClick}
            highlightInvestment={currentInvestment}
            highlightLocationAccount={currentLocationAccount}
            transactionQuantity={currentQuantity}
            enableQuantityWarning={enableQuantityWarning}
          />
        </Stack>
      )}

      {/* Array Fields */}
      {arrayFieldGroups.length > 0 && (
        <Stack gap="md">
          <Title order={5}>Array Fields</Title>
          {arrayFieldGroups.map(renderArrayField)}
        </Stack>
      )}

      {fieldsWithOverrides.length === 0 && arrayFieldGroups.length === 0 && (
        <Text ta="center" c="dimmed" py="xl">
          All fields in this record are hidden
        </Text>
      )}
    </Stack>
  );
}; 
